import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Activity,
  ShieldAlert,
  Pause,
  Play,
  RefreshCw,
  Users,
  Zap,
  AlertCircle,
  CheckCircle2,
  Clock,
  Search,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/observability`;

const SINCE_OPTIONS = [
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '1y', label: 'Last 1 year' },
];

const fmtDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'medium' });
  } catch {
    return String(v);
  }
};

const fmtShortTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(v);
  }
};

const KpiCard = ({ icon: Icon, label, value, sub, accent = 'slate', testid }) => (
  <Card className="border" data-testid={testid}>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
          <p className={`text-3xl font-bold mt-1 text-${accent}-700`}>{value}</p>
          {sub ? <p className="text-xs text-slate-500 mt-1">{sub}</p> : null}
        </div>
        <Icon className={`h-7 w-7 text-${accent}-500`} />
      </div>
    </CardContent>
  </Card>
);

export default function LiveMonitor() {
  const { user } = useAuth();
  const [since, setSince] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const [summary, setSummary] = useState(null);
  const [logins, setLogins] = useState(null);
  const [activity, setActivity] = useState(null);
  const [perf, setPerf] = useState(null);
  const [logs, setLogs] = useState(null);

  const [loginFilter, setLoginFilter] = useState({ success: 'all', email: '' });
  const [logFilter, setLogFilter] = useState({ level: 'all', q: '' });
  const [activityFilter, setActivityFilter] = useState({ action: '', entity: '' });

  const loadAll = useCallback(async () => {
    const opts = { withCredentials: true };
    try {
      const [s, l, a, p, lg] = await Promise.all([
        axios.get(`${API}/summary?since=${since}`, opts),
        axios.get(
          `${API}/login-history?since=${since}&limit=300${
            loginFilter.success !== 'all' ? `&success=${loginFilter.success}` : ''
          }${loginFilter.email ? `&email=${encodeURIComponent(loginFilter.email)}` : ''}`,
          opts
        ),
        axios.get(
          `${API}/activity?since=${since}&limit=300${
            activityFilter.action ? `&action=${encodeURIComponent(activityFilter.action)}` : ''
          }${activityFilter.entity ? `&entity=${encodeURIComponent(activityFilter.entity)}` : ''}`,
          opts
        ),
        axios.get(`${API}/performance?since=${since}`, opts),
        axios.get(
          `${API}/logs?lines=500${logFilter.level !== 'all' ? `&level=${logFilter.level}` : ''}${
            logFilter.q ? `&q=${encodeURIComponent(logFilter.q)}` : ''
          }`,
          opts
        ),
      ]);
      setSummary(s.data);
      setLogins(l.data);
      setActivity(a.data);
      setPerf(p.data);
      setLogs(lg.data);
    } catch (err) {
      // Quietly fail on auto-refresh; show toast only on manual triggers via the toast key
      toast.error(err?.response?.data?.detail || 'Failed to load monitor data', { id: 'monitor-err' });
    }
  }, [since, loginFilter, activityFilter, logFilter]);

  // Initial load + filter/since changes + auto-refresh while toggle is on
  useAutoRefresh(loadAll, 10_000, autoRefresh, [since, loginFilter, activityFilter, logFilter]);

  if (!user?.is_super_admin) {
    return (
      <div className="max-w-3xl mx-auto py-10" data-testid="monitor-noaccess">
        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-rose-600" />
            <p className="text-sm">This page is restricted to Super Admins.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 py-6" data-testid="live-monitor-page">
      {/* Header strip */}
      <div className="flex flex-wrap items-end gap-4 justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="h-7 w-7 text-emerald-600" />
            Live Monitor
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Logins, activity, performance, and backend logs — refreshes every 10 seconds while the
            toggle is on.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={since} onValueChange={setSince}>
            <SelectTrigger className="w-[170px]" data-testid="monitor-since-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SINCE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 border rounded-md px-3 py-1.5">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
              data-testid="monitor-auto-refresh-switch"
            />
            <Label htmlFor="auto-refresh" className="text-sm cursor-pointer">
              {autoRefresh ? (
                <span className="flex items-center gap-1 text-emerald-700">
                  <Play className="h-3.5 w-3.5" /> Live
                </span>
              ) : (
                <span className="flex items-center gap-1 text-slate-500">
                  <Pause className="h-3.5 w-3.5" /> Paused
                </span>
              )}
            </Label>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadAll}
            data-testid="monitor-refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={Users}
          label="Online now"
          value={summary?.online_now ?? '—'}
          sub={`${summary?.active_sessions ?? 0} active sessions`}
          accent="emerald"
          testid="kpi-online"
        />
        <KpiCard
          icon={CheckCircle2}
          label={`Logins (${since})`}
          value={summary?.logins?.total ?? '—'}
          sub={`${summary?.logins?.success ?? 0} ok · ${summary?.logins?.failed ?? 0} failed`}
          accent="blue"
          testid="kpi-logins"
        />
        <KpiCard
          icon={Zap}
          label={`Requests (${since})`}
          value={summary?.performance?.requests ?? '—'}
          sub={`p95 ${summary?.performance?.p95_ms ?? '—'} ms`}
          accent="violet"
          testid="kpi-requests"
        />
        <KpiCard
          icon={AlertCircle}
          label="Error rate"
          value={`${summary?.performance?.error_rate_pct ?? '—'}%`}
          sub={`${summary?.performance?.errors_5xx ?? 0} 5xx · ${summary?.performance?.warnings_4xx ?? 0} 4xx`}
          accent={(summary?.performance?.error_rate_pct ?? 0) > 1 ? 'rose' : 'slate'}
          testid="kpi-errors"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="monitor-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="logins" data-testid="tab-logins">Logins</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Logs</TabsTrigger>
        </TabsList>

        {/* OVERVIEW: timeseries + recent failures */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4" /> Requests & errors over time
              </CardTitle>
            </CardHeader>
            <CardContent className="h-72">
              {perf?.timeseries?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={perf.timeseries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="ts"
                      tickFormatter={(t) => fmtShortTime(t)}
                      stroke="#64748b"
                      fontSize={11}
                    />
                    <YAxis stroke="#64748b" fontSize={11} />
                    <Tooltip
                      labelFormatter={(t) => fmtDate(t)}
                      contentStyle={{ fontSize: 12, borderRadius: 6 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="requests" stackId="1" stroke="#7c3aed" fill="#ddd6fe" />
                    <Area type="monotone" dataKey="warnings" stackId="2" stroke="#d97706" fill="#fde68a" />
                    <Area type="monotone" dataKey="errors" stackId="3" stroke="#dc2626" fill="#fecaca" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full grid place-items-center text-sm text-slate-400">
                  Collecting data… this chart populates as users hit the API
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent failed logins</CardTitle>
            </CardHeader>
            <CardContent>
              {logins?.entries?.filter((e) => !e.success).slice(0, 10).length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>IP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logins.entries.filter((e) => !e.success).slice(0, 10).map((e) => (
                      <TableRow key={e.id}>
                        <TableCell className="text-xs">{fmtDate(e.created_at)}</TableCell>
                        <TableCell className="text-xs font-medium">{e.email}</TableCell>
                        <TableCell><Badge variant="destructive">{e.reason}</Badge></TableCell>
                        <TableCell className="text-xs">{e.ip || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-slate-500 italic">No failed logins in this window.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOGINS */}
        <TabsContent value="logins" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={loginFilter.success}
              onValueChange={(v) => setLoginFilter((f) => ({ ...f, success: v }))}
            >
              <SelectTrigger className="w-[160px]" data-testid="login-success-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All attempts</SelectItem>
                <SelectItem value="true">Success only</SelectItem>
                <SelectItem value="false">Failures only</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                placeholder="Filter by email…"
                value={loginFilter.email}
                onChange={(e) => setLoginFilter((f) => ({ ...f, email: e.target.value }))}
                className="pl-8 w-64"
                data-testid="login-email-filter"
              />
            </div>
            <Badge variant="secondary" className="ml-auto">
              {logins?.total ?? 0} attempt(s)
            </Badge>
          </div>
          <Card>
            <div className="overflow-x-auto" data-testid="login-history-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>User-Agent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logins?.entries?.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(e.created_at)}</TableCell>
                      <TableCell className="text-xs font-medium">{e.email}</TableCell>
                      <TableCell>
                        {e.success ? (
                          <Badge className="bg-emerald-100 text-emerald-900">success</Badge>
                        ) : (
                          <Badge variant="destructive">failed</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{e.reason || '—'}</TableCell>
                      <TableCell className="text-xs">{e.ip || '—'}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[300px] truncate" title={e.user_agent}>
                        {e.user_agent || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* ACTIVITY */}
        <TabsContent value="activity" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Filter by action (e.g. create, login)…"
              value={activityFilter.action}
              onChange={(e) => setActivityFilter((f) => ({ ...f, action: e.target.value }))}
              className="w-64"
              data-testid="activity-action-filter"
            />
            <Input
              placeholder="Filter by entity (e.g. invoice, vendor)…"
              value={activityFilter.entity}
              onChange={(e) => setActivityFilter((f) => ({ ...f, entity: e.target.value }))}
              className="w-64"
              data-testid="activity-entity-filter"
            />
            <Badge variant="secondary" className="ml-auto">
              {activity?.total ?? 0} event(s)
            </Badge>
          </div>
          <Card>
            <div className="overflow-x-auto" data-testid="activity-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Entity ID</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activity?.entries?.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(e.created_at)}</TableCell>
                      <TableCell className="text-xs font-medium">{e.email || e.user_id || '—'}</TableCell>
                      <TableCell><Badge variant="outline">{e.action}</Badge></TableCell>
                      <TableCell className="text-xs">{e.entity}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[160px] truncate" title={e.entity_id}>
                        {e.entity_id || '—'}
                      </TableCell>
                      <TableCell className="text-[11px] text-slate-500 max-w-[260px] truncate">
                        {e.details ? JSON.stringify(e.details) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* PERFORMANCE */}
        <TabsContent value="performance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Average vs max latency (ms)</CardTitle>
            </CardHeader>
            <CardContent className="h-64">
              {perf?.timeseries?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={perf.timeseries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="ts" tickFormatter={(t) => fmtShortTime(t)} stroke="#64748b" fontSize={11} />
                    <YAxis stroke="#64748b" fontSize={11} />
                    <Tooltip labelFormatter={(t) => fmtDate(t)} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="avg_latency_ms" stroke="#0ea5e9" fill="#bae6fd" />
                    <Area type="monotone" dataKey="max_latency_ms" stroke="#ef4444" fill="#fecaca" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full grid place-items-center text-sm text-slate-400">No data yet.</div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top endpoints by traffic</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Method</TableHead>
                    <TableHead>Path</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Errors (5xx)</TableHead>
                    <TableHead className="text-right">Avg latency (ms)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {perf?.top_endpoints?.map((t, i) => (
                    <TableRow key={i}>
                      <TableCell><Badge variant="outline">{t.method}</Badge></TableCell>
                      <TableCell className="text-xs font-mono">{t.path}</TableCell>
                      <TableCell className="text-right">{t.requests}</TableCell>
                      <TableCell className={`text-right ${t.errors > 0 ? 'text-rose-700 font-medium' : ''}`}>
                        {t.errors}
                      </TableCell>
                      <TableCell className="text-right">{t.avg_latency_ms}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOGS */}
        <TabsContent value="logs" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={logFilter.level} onValueChange={(v) => setLogFilter((f) => ({ ...f, level: v }))}>
              <SelectTrigger className="w-[140px]" data-testid="log-level-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All levels</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                placeholder="Search log text…"
                value={logFilter.q}
                onChange={(e) => setLogFilter((f) => ({ ...f, q: e.target.value }))}
                className="pl-8 w-80"
                data-testid="log-search-input"
              />
            </div>
            <Badge variant="secondary" className="ml-auto">
              {logs?.total ?? 0} line(s)
            </Badge>
          </div>
          <Card className="bg-slate-950 border-slate-800">
            <CardContent className="pt-6 pb-6">
              <div
                className="font-mono text-[11px] leading-relaxed text-slate-200 max-h-[60vh] overflow-y-auto space-y-0.5"
                data-testid="logs-panel"
              >
                {logs?.lines?.length ? (
                  logs.lines.map((l, i) => (
                    <div
                      key={i}
                      className={`flex gap-2 hover:bg-slate-800/40 px-2 py-0.5 rounded ${
                        l.level === 'error' ? 'text-rose-300' :
                        l.level === 'warning' ? 'text-amber-300' :
                        l.level === 'info' ? 'text-slate-300' : 'text-slate-400'
                      }`}
                    >
                      <span className="text-slate-500 shrink-0">[{l.file}]</span>
                      <span className="whitespace-pre-wrap break-all">{l.line}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 italic">No log lines match.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
